// shared/utils/format.ts

import { TransactionType } from '@luminaria/shared';
import { formatDateDisplay, formatTimestamp } from './dates';

export { formatTimestamp };

/**
 * Option action choices for dropdowns/selects
 * These are the four primary option trading actions
 */
export const OPTION_ACTION_OPTIONS = [
  { value: TransactionType.BuyToOpen, label: 'Buy to Open' },
  { value: TransactionType.BuyToClose, label: 'Buy to Close' },
  { value: TransactionType.SellToOpen, label: 'Sell to Open' },
  { value: TransactionType.SellToClose, label: 'Sell to Close' },
] as const;

// The number formatters live in luminaria-shared (one implementation for web,
// API and MCP). Re-exported here during migration so existing
// `@shared/utils/format` imports keep working; new code may import either.
export {
  formatCompactNumber,
  formatCurrency,
  formatCurrencyCompact,
  formatNumber,
  formatPercent,
  formatQuantity,
  formatRate,
} from '@luminaria/shared';
export type {
  FormatCurrencyOptions,
  FormatFallbackOptions,
  FormatNumberOptions,
  FormatPercentOptions,
} from '@luminaria/shared';

export interface SignClassOptions {
  /** Class for values above zero (default 'text-success') */
  positive?: string;
  /** Class for values below zero (default 'text-danger') */
  negative?: string;
  /** Class for zero, null and NaN (default '' — inherit) */
  zero?: string;
}

/** signClass options that flag only negatives (red) and leave zero/positive in body text. */
export const NEGATIVE_ONLY_SIGN_CLASSES: SignClassOptions = { positive: 'text-text', zero: 'text-text' };

/**
 * Tailwind text color for a signed value (green above zero, red below), for
 * places that can't wrap the value in `<ColoredValue>` (a table cell's or
 * metric card's class prop). Pass `{ positive: 'text-text' }` to flag only
 * negatives.
 */
export function signClass(
  value: number | null | undefined,
  { positive = 'text-success', negative = 'text-danger', zero = '' }: SignClassOptions = {},
): string {
  if (value == null || Number.isNaN(value) || value === 0) return zero;
  return value > 0 ? positive : negative;
}

/**
 * Format action names to be more readable with proper trading terminology
 * @param action - Raw action string (e.g., "buy", "transfer_in", "option_open_buy")
 * @returns Formatted action string (e.g., "Buy", "Transfer In", "Sell to Open")
 */
export function formatAction(action: string | undefined | null): string {
  if (!action) return '';
  
  // Map option actions to standard trading terminology
  const actionMap: Record<string, string> = {
    'sell_to_open': 'Sell to Open',
    'buy_to_open': 'Buy to Open',
    'sell_to_close': 'Sell to Close',
    'buy_to_close': 'Buy to Close',
    'option_open_sell': 'Sell to Open',
    'option_open_buy': 'Buy to Open',
    'option_close_sell': 'Sell to Close',
    'option_close_buy': 'Buy to Close',
    'option_assignment': 'Assignment',
    'assigned': 'Assignment',
    'option_expiration': 'Expiration',
    'option_expire': 'Expiration',
    'expired': 'Expiration',
    'fpl_income': 'Dividend',
  };
  
  const lowerAction = action.toLowerCase();
  if (actionMap[lowerAction]) {
    return actionMap[lowerAction];
  }
  
  return action
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Format a date for display
 * Uses centralized date utilities to avoid timezone issues.
 * @param date - Date string, Date object, or timestamp
 * @param options - Formatting options
 * @returns Formatted date string (e.g., "Dec 22, 2025" or "12/22/2025")
 */
export function formatDate(
  date: string | Date | number | undefined | null,
  options: { style?: 'short' | 'medium' | 'long' } = {}
): string {
  return formatDateDisplay(date, options.style);
}
